"use client"

import * as React from "react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface TooltipWrapperProps {
  children: React.ReactElement
  content: React.ReactNode
  disabled?: boolean
  showOnlyWhenDisabled?: boolean
  asChild?: boolean
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
}

/**
 * A wrapper component that enables tooltips on disabled elements.
 * 
 * This solves the common issue where Radix UI tooltips don't work on disabled elements.
 * It wraps the element in a span and applies the tooltip to the wrapper instead.
 * 
 * @param children - The element to wrap (typically a Button)
 * @param content - The tooltip content
 * @param disabled - Whether the wrapped element is disabled
 * @param showOnlyWhenDisabled - Only show tooltip when disabled (useful for explaining why something is disabled)
 * @param asChild - Pass through to TooltipTrigger
 * @param side - Tooltip position
 * @param align - Tooltip alignment
 */
export function TooltipWrapper({
  children,
  content,
  disabled = false,
  showOnlyWhenDisabled = false,
  asChild = true,
  side,
  align,
}: TooltipWrapperProps) {
  // If showOnlyWhenDisabled is true and element is not disabled, just return the child
  if (showOnlyWhenDisabled && !disabled) {
    return children
  }

  // If the element is disabled, we need to wrap it to enable tooltips
  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild={asChild}>
            <span className="inline-block">
              {children}
            </span>
          </TooltipTrigger>
          <TooltipContent side={side} align={align}>
            {content}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // For non-disabled elements, use the normal tooltip pattern
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild={asChild}>
          {children}
        </TooltipTrigger>
        <TooltipContent side={side} align={align}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}